import {
  Alert,
  Box,
  IconButton,
  InputBase,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import type {
  ConversationResponse,
  DirectMessageAttachmentResponse,
  MessageResponse,
} from "@bw/shared/types/generated";
import { useSendMessage } from "../hooks/useSendMessage";
import { useUploadMessageAttachment } from "../hooks/useUploadMessageAttachment";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

const MAX_ATTACHMENTS = 4;
const MAX_PARALLEL_UPLOADS = 2;
const ACCEPTED_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
].join(",");

type AttachmentDraft = {
  localId: string;
  idempotencyKey: string;
  file: File;
  status: "queued" | "uploading" | "ready" | "error";
  progress: number | null;
  previewUrl?: string;
  attachment?: DirectMessageAttachmentResponse;
};

export type MessageComposerProps = {
  conversationId: string;
  communityId?: string;
  disabled: boolean;
  writeAccess: ConversationResponse["write_access"] | undefined;
  /** Presentation only: requester exhausted the server's pending inquiry allowance. */
  readOnlyReason?: "awaiting_staff_reply";
  compact?: boolean;
  /** Called only after Social confirms the outgoing message. */
  onMessageSent?: (message: MessageResponse) => void;
  initialDraft?: string;
  /** "Message #general" / "Message Mei Wong"; the field's name stays "Write a message". */
  placeholder?: string;
};

/**
 * Shared direct-message composer for the full workspace and dock. Browser-side
 * acceptance is just a useful picker hint: the backend remains the only size,
 * type, image-normalization, retention, and authorization authority.
 */
export function MessageComposer({
  conversationId,
  communityId,
  disabled: externallyDisabled,
  writeAccess,
  readOnlyReason,
  compact = false,
  onMessageSent,
  initialDraft = "",
  placeholder,
}: MessageComposerProps) {
  // Fail closed while the server's conversation projection is unavailable.
  // Participant counts and cached friendship cards are not write authority.
  const disabled = externallyDisabled || writeAccess !== "allowed";
  const [content, setContent] = useState(initialDraft);
  const contentEditVersion = useRef(0);
  const [attachments, setAttachments] = useState<AttachmentDraft[]>([]);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const attachmentDraftsRef = useRef<AttachmentDraft[]>([]);
  const attachmentCountRef = useRef(0);
  const previewUrlsRef = useRef(new Set<string>());
  const inputId = useId();
  const sendMessage = useSendMessage(conversationId, communityId);
  const { mutateAsync: uploadAttachment } = useUploadMessageAttachment(
    conversationId,
    communityId,
  );

  const trimmed = content.trim();
  const readyAttachmentIds = attachments.flatMap((item) =>
    item.status === "ready" && item.attachment
      ? [item.attachment.attachment_id]
      : [],
  );
  const uploadsInFlight = attachments.some(
    (item) => item.status === "queued" || item.status === "uploading",
  );
  const canSend =
    !disabled &&
    !uploadsInFlight &&
    !sendMessage.isPending &&
    (trimmed.length > 0 || readyAttachmentIds.length > 0);

  useEffect(() => {
    attachmentDraftsRef.current = attachments;
    attachmentCountRef.current = attachments.length;
  }, [attachments]);

  useEffect(
    () => () => {
      for (const previewUrl of previewUrlsRef.current) {
        if (typeof URL.revokeObjectURL === "function") {
          URL.revokeObjectURL(previewUrl);
        }
      }
      previewUrlsRef.current.clear();
    },
    [],
  );

  function releasePreview(previewUrl: string | undefined): void {
    if (!previewUrl) return;
    previewUrlsRef.current.delete(previewUrl);
    if (typeof URL.revokeObjectURL === "function") {
      URL.revokeObjectURL(previewUrl);
    }
  }

  function clearAttachments(): void {
    for (const attachment of attachmentDraftsRef.current) {
      releasePreview(attachment.previewUrl);
    }
    attachmentDraftsRef.current = [];
    attachmentCountRef.current = 0;
    setAttachments([]);
  }

  function removeAttachment(localId: string): void {
    const draft = attachmentDraftsRef.current.find(
      (item) => item.localId === localId,
    );
    if (draft) {
      releasePreview(draft.previewUrl);
      attachmentDraftsRef.current = attachmentDraftsRef.current.filter(
        (item) => item.localId !== localId,
      );
      attachmentCountRef.current = attachmentDraftsRef.current.length;
    }
    setAttachments((current) =>
      current.filter((item) => item.localId !== localId),
    );
  }

  const uploadDraft = useCallback(
    (draft: AttachmentDraft): void => {
      setAttachments((current) =>
        current.map((item) =>
          item.localId === draft.localId
            ? { ...item, status: "uploading", progress: null }
            : item,
        ),
      );
      // `useMutation().mutate` replaces its per-call observer when invoked
      // again. Multiple selected files therefore need their own promises: every
      // resolution below settles its matching draft even when another upload
      // completes first.
      void uploadAttachment({
        file: draft.file,
        idempotencyKey: draft.idempotencyKey,
        onProgress: (progress) => {
          setAttachments((current) =>
            current.map((item) =>
              item.localId === draft.localId && item.status === "uploading"
                ? { ...item, progress }
                : item,
            ),
          );
        },
      }).then(
        (attachment) => {
          setAttachments((current) =>
            current.map((item) =>
              item.localId === draft.localId
                ? { ...item, status: "ready", progress: 100, attachment }
                : item,
            ),
          );
        },
        () => {
          setAttachments((current) =>
            current.map((item) =>
              item.localId === draft.localId
                ? { ...item, status: "error", progress: null }
                : item,
            ),
          );
        },
      );
    },
    [uploadAttachment],
  );

  useEffect(() => {
    if (disabled) return;
    const active = attachments.filter(
      (item) => item.status === "uploading",
    ).length;
    const slots = MAX_PARALLEL_UPLOADS - active;
    if (slots <= 0) return;
    attachments
      .filter((item) => item.status === "queued")
      .slice(0, slots)
      .forEach(uploadDraft);
  }, [attachments, disabled, uploadDraft]);

  function onPick(event: ChangeEvent<HTMLInputElement>): void {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!picked.length || disabled) return;
    const remaining = MAX_ATTACHMENTS - attachmentCountRef.current;
    const accepted = picked.slice(0, Math.max(remaining, 0));
    if (accepted.length !== picked.length) {
      setPickerError(
        `You can add up to ${MAX_ATTACHMENTS} attachments to one message.`,
      );
    } else {
      setPickerError(null);
    }
    const drafts = accepted.map((file) => {
      const previewUrl =
        file.type.startsWith("image/") &&
        typeof URL.createObjectURL === "function"
          ? URL.createObjectURL(file)
          : undefined;
      if (previewUrl) previewUrlsRef.current.add(previewUrl);
      return {
        localId: globalThis.crypto.randomUUID(),
        idempotencyKey: globalThis.crypto.randomUUID(),
        file,
        status: "queued" as const,
        progress: null,
        previewUrl,
      };
    });
    attachmentDraftsRef.current = [...attachmentDraftsRef.current, ...drafts];
    attachmentCountRef.current = attachmentDraftsRef.current.length;
    setAttachments((current) => [...current, ...drafts]);
  }

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!canSend) return;
    const submittedEditVersion = contentEditVersion.current;
    sendMessage.mutate(
      {
        content,
        attachment_ids: readyAttachmentIds,
      },
      {
        onSuccess: (message) => {
          // The textbox stays editable while sending. Only clear the draft
          // that was submitted, including when a new draft repeats its text.
          if (contentEditVersion.current === submittedEditVersion) {
            setContent("");
          }
          clearAttachments();
          setPickerError(null);
          onMessageSent?.(message);
        },
      },
    );
  }

  return (
    <Stack
      component="form"
      spacing={0.75}
      onSubmit={onSubmit}
      sx={{
        px: compact ? 1 : { xs: 1.5, sm: 3 },
        py: compact ? 1 : 1.5,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      {sendMessage.isError ? (
        <GuardianAwareError error={sendMessage.error}>
          <Alert severity="error" role="alert">
            We couldn&apos;t send your message. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}
      {pickerError ? (
        <Alert severity="warning" role="alert">
          {pickerError}
        </Alert>
      ) : null}
      {writeAccess === "friendship_required" ? (
        <Alert severity="info">
          You must be friends to send messages or files.
        </Alert>
      ) : writeAccess === "read_only" ? (
        <Alert severity="info">
          {readOnlyReason === "awaiting_staff_reply"
            ? "Wait for a staff reply to continue."
            : "This conversation is read-only."}
        </Alert>
      ) : null}
      {attachments.length ? (
        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary">
            Attachments expire 30 days after sending. Unsent uploads are removed
            after one hour.
          </Typography>
          <Stack
            component="ul"
            spacing={0.75}
            sx={{ listStyle: "none", m: 0, p: 0 }}
          >
            {attachments.map((attachment) => (
              <Stack
                key={attachment.localId}
                component="li"
                direction="row"
                spacing={0.75}
                sx={{ alignItems: "center", minWidth: 0 }}
              >
                {attachment.previewUrl ? (
                  <Box
                    component="img"
                    src={attachment.previewUrl}
                    alt={attachment.file.name}
                    sx={{
                      width: 56,
                      height: 56,
                      flex: "0 0 auto",
                      borderRadius: 1,
                      objectFit: "cover",
                      bgcolor: "background.default",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      display: "grid",
                      flex: "0 0 auto",
                      placeItems: "center",
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  >
                    <AttachFileRoundedIcon
                      fontSize="small"
                      aria-hidden="true"
                    />
                  </Box>
                )}
                <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ alignItems: "center" }}
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      {attachment.file.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      role={attachment.status === "error" ? "alert" : "status"}
                      aria-live={
                        attachment.status === "error" ? "assertive" : "polite"
                      }
                    >
                      {attachment.status === "queued"
                        ? "Waiting…"
                        : attachment.status === "uploading"
                          ? attachment.progress === null
                            ? "Uploading…"
                            : attachment.progress === 100
                              ? "Processing…"
                              : `${attachment.progress}%`
                          : attachment.status === "error"
                            ? "Couldn't upload"
                            : "Ready"}
                    </Typography>
                    {attachment.status === "error" ? (
                      <IconButton
                        type="button"
                        size="small"
                        aria-label={`Retry ${attachment.file.name}`}
                        onClick={() =>
                          setAttachments((current) =>
                            current.map((item) =>
                              item.localId === attachment.localId
                                ? { ...item, status: "queued", progress: null }
                                : item,
                            ),
                          )
                        }
                        disabled={disabled || sendMessage.isPending}
                      >
                        <ReplayRoundedIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                    <IconButton
                      type="button"
                      size="small"
                      aria-label={`Remove ${attachment.file.name}`}
                      onClick={() => removeAttachment(attachment.localId)}
                      disabled={sendMessage.isPending}
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  {attachment.status === "uploading" ? (
                    attachment.progress === null ? (
                      <LinearProgress
                        aria-label={`Uploading ${attachment.file.name}`}
                        aria-valuetext="Upload progress unavailable"
                        variant="indeterminate"
                      />
                    ) : (
                      <LinearProgress
                        aria-label={`Uploading ${attachment.file.name}`}
                        aria-valuetext={
                          attachment.progress === 100
                            ? "Processing attachment"
                            : `${attachment.progress}% uploaded`
                        }
                        variant="determinate"
                        value={attachment.progress}
                      />
                    )
                  ) : null}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
      ) : null}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.25,
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
          "&:focus-within": { borderColor: "primary.main" },
        }}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={ACCEPTED_ATTACHMENT_TYPES}
          multiple
          hidden
          onChange={onPick}
          disabled={disabled || sendMessage.isPending}
        />
        <IconButton
          type="button"
          aria-label="Add image or file"
          onClick={() => inputRef.current?.click()}
          disabled={
            disabled ||
            sendMessage.isPending ||
            attachments.length >= MAX_ATTACHMENTS
          }
        >
          <AddRoundedIcon fontSize="small" />
        </IconButton>
        <InputBase
          id={compact ? undefined : "thread-compose-input"}
          type="text"
          value={content}
          onChange={(event) => {
            contentEditVersion.current += 1;
            setContent(event.target.value);
          }}
          placeholder={placeholder ?? "Write a message…"}
          autoComplete="off"
          disabled={disabled}
          inputProps={{ "aria-label": "Write a message" }}
          sx={{ flex: 1, minWidth: 0, py: 0.75 }}
        />
        <IconButton
          type="submit"
          color="primary"
          aria-label="Send"
          disabled={!canSend}
        >
          <SendRoundedIcon />
        </IconButton>
      </Box>
    </Stack>
  );
}
